import type { Client } from '@optimizely/optimizely-sdk';
import { trackOptimizelyEvent } from '@/app_lib/optimizely/utils';
import type { GetAccountSuccess } from '@/app_lib/types/accounts/piano/pianoApi';
import type { Tinypass } from '@/app_lib/types/accounts/piano/tinypass';
import type { DataLayerEventName, EventData } from '@/app_lib/types/Gtm';

interface TemplateIntersectionProps {
  container: Element;
  optimizely?: Client;
  optimizelyEvent?: string;
  sendEventOnConsent: (eventData: EventData, eventType?: DataLayerEventName) => void;
  threshold?: number;
  trackingPayload: TemplateViewTrackingPayload;
}

export interface TemplateViewTrackingPayload {
  displayMode: string;
  experienceActionId: string;
  experienceId: string;
  templateId: string;
  templateVariantId: string | null;
  eventAction: string;
  eventCategory: string;
  eventLabel: string;
}

export async function getExistingPianoUid(email: string): Promise<string | null> {
  const checkAccountResponse = await fetch('/api/checkPianoAccountExists', {
    body: JSON.stringify({ email }),
    headers: { 'Content-Type': 'application/json' },
    method: 'POST',
  });

  if (!checkAccountResponse.ok) {
    if (checkAccountResponse.status === 404) {
      // Successful api call, but no user found
      // Saving space if we wanted to do something different here in the future
      return null;
    }
    // Where we would do something if the Piano API call fails
    // Saving space if we wanted to do something different here in the future
    return null;
  }

  const userData: GetAccountSuccess = await checkAccountResponse.json();

  return userData.getUserResponse.uid;
}

export const getLts = (tp: Tinypass, experienceId: string, eventType: string): string => {
  const experienceResult = tp.experience._getLastExecutionResult();
  const userSegments = experienceResult.result?.events.find(
    (event) => event.eventType === eventType && event.eventExecutionContext.experienceId === experienceId,
  )?.eventExecutionContext.userSegments;
  const lts = userSegments?.COMPOSER1X
    ? userSegments?.COMPOSER1X.segments.find((score) => score.startsWith('LTs'))
    : 'no_score';

  return lts || 'no_score';
};

export const PROMO_CONTAINER_OPTIMIZELY_EVENTS = {
  // TODO: Uncomment other templates once their containers are created
  '#promo-piano-template-recirc-card-container-latest': 'saw_promo_recirc_card',
  '#promo-piano-template-recirc-card-container-most-popular': 'saw_promo_recirc_card',
  '#promo-piano-template-right-rail-container': 'saw_promo_right_rail',
  // '#promo-piano-template-top-bar-container': 'saw_promo_top_bar',
};

export const buildTemplateIntersectionObserver = ({
  container,
  optimizely,
  optimizelyEvent,
  sendEventOnConsent,
  threshold = 0.5,
  trackingPayload,
}: TemplateIntersectionProps) => {
  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          sendEventOnConsent(trackingPayload);
          // Track template exposure to Optimizely if provided
          if (optimizely && optimizelyEvent) {
            trackOptimizelyEvent(optimizely, optimizelyEvent, {});
          }
          obs?.disconnect?.();
        }
      });
    },
    { threshold },
  );
  observer.observe(container);
};
